import { Fragment, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Skeleton, Tooltip, CircularProgress } from '@mui/material';
import {
  flowSteps,
  flowsQuery,
  FlowByIdVersionEnum,
  FlowResourceType,
  useAppStore,
  GlobalAddModalEnum,
  useManageFlowModalData,
  useEditorStore,
  SettingsIcon,
} from '@diana/core';
import { useFlowBuilder2PageContext } from '../../Context/useFlowBuilder2PageContext';
import { StyledIconButton } from '../../FlowBuilder2Page.styles';
import { FLOW_BUILDER2_PAGE_NS } from '../../i18n';

const useFlowSettingsIconButton = () => {
  const { reload } = useFlowBuilder2PageContext();

  const flowId = useEditorStore((s) => s.flow?.id ?? '');

  const globalAddModal = useAppStore((s) => s.globalAddModal);
  const setGlobalAddModal = useAppStore((s) => s.setGlobalAddModal);
  const setDynamicModalTab = useAppStore((s) => s.setDynamicModalTab);
  const setFlowOfAppStore = useAppStore((s) => s.setFlow);

  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: async (_args: { name: string; label: string }) => {
      const flow = await flowsQuery
        .getById({ id: flowId, version: FlowByIdVersionEnum.Specific })
        .fn();

      const getStepsQuery = flowSteps.getById({ id: flow.id });
      flow.steps = await getStepsQuery.fn();
      queryClient.setQueriesData({ queryKey: getStepsQuery.key }, flow.steps);

      return flow;
    },
    onSuccess: (data, args) => {
      setFlowOfAppStore(data);
      setGlobalAddModal(GlobalAddModalEnum.ManageFlow);
      setDynamicModalTab(args);
    },
  });

  useEffect(() => {
    if (globalAddModal === GlobalAddModalEnum.ManageFlow) {
      return () => {
        reload();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalAddModal]);

  return {
    onClick: mutate,
    isLoading: isPending,
  };
};

const ManageWorkflowButton = () => {
  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);

  const { onClick, isLoading } = useFlowSettingsIconButton();
  const { workflowTabs } = useManageFlowModalData();

  const { name, label } = workflowTabs[0].options[0];

  return (
    <Tooltip title={t('tooltips.settings')}>
      <StyledIconButton disabled={isLoading} onClick={() => onClick({ name, label })}>
        {isLoading ? <CircularProgress size={20} /> : <SettingsIcon fontSize="small" />}
      </StyledIconButton>
    </Tooltip>
  );
};

const ManageAssetTypeButton = () => {
  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);
  const { onClick, isLoading } = useFlowSettingsIconButton();
  const { assetTypeTabs } = useManageFlowModalData();

  const { name, label } = assetTypeTabs[0].options[0];

  return (
    <Tooltip title={t('tooltips.settings')}>
      <StyledIconButton disabled={isLoading} onClick={() => onClick({ name, label })}>
        {isLoading ? <CircularProgress size={20} /> : <SettingsIcon fontSize="small" />}
      </StyledIconButton>
    </Tooltip>
  );
};

export const FlowSettingsIconButton = () => {
  const { isLoading: isLoadingPage, error: pageError } = useFlowBuilder2PageContext();

  const flowResourceType = useEditorStore((s) => s.flow?.properties.flowResourceType);

  if (isLoadingPage) return <Skeleton variant="circular" width={32} height={32} />;
  if (pageError) return <Fragment />;
  if (flowResourceType === FlowResourceType.Entity) return <ManageAssetTypeButton />;

  return <ManageWorkflowButton />;
};
